// src/App.js - VERSÃO CORRIGIDA - LOCUTORCONTROL PERSISTENTE
// ✅ LocutorControl agora usa toda largura disponível
// ✅ LocutorControl NÃO é desmontado ao trocar de tela (apenas ocultado)

import React, { useState, useEffect } from 'react';
import { addWebSocketListener, removeWebSocketListener } from './services/websocketSingleton';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import Dashboard from './components/layout/Dashboard';
import LoginScreen from './screens/LoginScreen';
import InviteSignupScreen from './screens/InviteSignupScreen';

// Screens
import CadastroScreen from './screens/CadastroScreen';
import SystemScreen from './screens/SystemScreen';
import ResultadoScreen from './screens/ResultadoScreen';
import EventosScreen from './screens/EventosScreen';
// Componentes
import MiniPlayer from './components/player/MiniPlayer';
import LocutorControl from './components/locutor/LocutorControl';
import AudioSyncInitializer from './components/synchronizers/AudioSyncInitializer';
import TimeConfigControl from './components/config/TimeConfigControl';
import StoreMapControl from './components/StoreMapControl';
import PesoSecoesScreen from './screens/PesoSecoesScreen';
import SemanticReviewScreen from './screens/SemanticReviewScreen';
import LocutorMonitorScreen from './screens/LocutorMonitorScreen';

// Hooks — useSpotQueue é gerenciado pelo AppContext; não instanciar aqui

// Styles
import './styles/global.css';
import './styles/components.css';

// ============================================
// ROTA PRIVADA — redireciona para /login se não autenticado
// ============================================
function PrivateRoute({ children }) {
  const [state, setState] = useState({ checking: true, ok: false });

  useEffect(() => {
    let cancelled = false;

    async function validateSession() {
      try {
        const res = await fetch('/api/auth/session', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (res.ok && data.perfil?.slug) {
          localStorage.removeItem('radio-ai-jwt');
          localStorage.setItem('radio-ai-user', JSON.stringify(data.perfil));
          setState({ checking: false, ok: true });
          return;
        }
      } catch {}

      if (!cancelled) {
        localStorage.removeItem('radio-ai-jwt');
        localStorage.removeItem('radio-ai-user');
        setState({ checking: false, ok: false });
      }
    }

    validateSession();
    return () => { cancelled = true; };
  }, []);

  if (state.checking) return null;
  if (!state.ok) return <Navigate to="/login" replace />;
  return children;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function AppContent() {
  const { tela, setTela, spotQueue, clearQueue } = useAppContext();
  const [lojaFechada, setLojaFechada] = useState(false);
  const [showClock, setShowClock] = useState(false);
  const [screenContext, setScreenContext] = useState(null);

  // Lê clienteId uma vez para filtragem de mensagens WS
  const clienteId = (() => {
    try { return JSON.parse(localStorage.getItem('radio-ai-user') || '{}').id || null; }
    catch { return null; }
  })();

  useEffect(() => {
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.clienteId !== clienteId) return;
        if (msg.type === 'FECHAR_LOJA') setLojaFechada(true);
        if (msg.type === 'LOCUTOR_SCHEDULER_ATIVADO') setLojaFechada(false);
      } catch {}
    };
    addWebSocketListener(handler, 'LojaFechadaBanner');
    return () => removeWebSocketListener(handler, 'LojaFechadaBanner');
  }, [clienteId]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const isIndoor = (() => {
        try { return JSON.parse(localStorage.getItem('radio-ai-user') || '{}').tipoProduto === 'indoor'; }
        catch { return false; }
      })();
      if (isIndoor) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ============================================
  // NAVEGAÇÃO
  // ============================================
  const handleNavigate = (destination, context = null) => {
    setScreenContext(context || null);
    setTela(destination);
  };

  // ============================================
  // RENDER SCREENS
  // ============================================
  const renderContent = () => {
    switch (tela) {
      case 'cadastro':
        return <CadastroScreen />;
      case 'Sistema':
        return <SystemScreen />;
      //case 'inicial':
      //  return <InicialScreen />;
      case 'resultado':
        return <ResultadoScreen />;
      case 'eventos':
        return <EventosScreen />;
      // 🎙️ Locutor
      // IMPORTANTE: agora o LocutorControl é renderizado fora do switch,
      // para não ser desmontado. Aqui não renderizamos nada.
      case 'LocutorControl':
        return null;

      // 🗺️ Mapa da Loja
      case 'StoreMapControl': {
        const u = (() => { try { return JSON.parse(localStorage.getItem('radio-ai-user') || '{}'); } catch { return {}; } })();
        return (
          <StoreMapControl
            clienteId={u.id}
            focusSectionRef={screenContext?.focusSectionRef || null}
            focusSectionName={screenContext?.focusSectionName || null}
            focusSource={screenContext?.source || null}
          />
        );
      }

      case 'PesoSecoes':
        return <PesoSecoesScreen />;

      case 'SemanticReview':
        return <SemanticReviewScreen onNavigate={handleNavigate} />;

      case 'LocutorMonitor':
        return <LocutorMonitorScreen />;

      default:
        return <CadastroScreen />;
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      {/* Inicializadores */}
      <AudioSyncInitializer />
      <TimeConfigControl clienteId={clienteId} isOpen={showClock} onClose={() => setShowClock(false)} />

      {/* Dashboard */}
      <Dashboard
        currentScreen={tela}
        onNavigate={handleNavigate}
        spotQueue={spotQueue}
        clearQueue={clearQueue}
        onShowClock={() => setShowClock(true)}
        player={<MiniPlayer />}
      >

        {/* Banner de loja fechada */}
        {lojaFechada && (
          <div style={{
            background: '#1e293b',
            color: 'white',
            padding: '10px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderLeft: '4px solid #334155',
            flexWrap: 'wrap',
          }}>
            <span>🌙 Loja fechada — sistema pausado.</span>
            <span>
              Horário incorreto?{' '}
              <button
                onClick={() => setTela('cadastro')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                  fontSize: '14px',
                  fontFamily: 'inherit',
                }}
              >
                Vá em Configurações
              </button>
              {' '}para ajustar.
            </span>
          </div>
        )}

        <div style={{ display: tela === 'LocutorControl' ? 'block' : 'none' }}>
          <LocutorControl />
        </div>
        {tela !== 'LocutorControl' && renderContent()}

      </Dashboard>
    </>
  );
}

// ============================================
// APP WRAPPER
// ============================================
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          <LoginScreen onLogin={(perfil) => {
            window.location.href = `/cliente/${perfil.slug}`;
          }} />
        } />
        <Route path="/convite/:token" element={<InviteSignupScreen />} />
        <Route path="/cliente/:slug" element={
          <PrivateRoute>
            <AppProvider>
              <AppContent />
            </AppProvider>
          </PrivateRoute>
        } />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
